import { HttpParams } from '@angular/common/http'
import { Component, ViewChild } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { WjChartModule } from '@grapecity/wijmo.angular2.chart'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { FlexChart, IRenderEngine } from '@grapecity/wijmo.chart'
import { CellType, Column, GridPanel } from '@grapecity/wijmo.grid'
import { NgbNav, NgbNavContent, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase, NgbNavOutlet } from '@ng-bootstrap/ng-bootstrap'

import { BaseComponent, FeatureType, Grid, ListItem } from '../../shared'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { adjGrossMarginPctComponent } from './adj-gross-margin-pct/adj-gross-margin-pct.component'
import { EstimateVsActualComponent } from './estimate-vs-actual/estimate-vs-actual.component'
import { MarginEstVsActualComponent } from './margin-est-vs-actual/margin-est-vs-actual.component'

@Component({
    templateUrl: './analytics.component.html',
    styleUrls: ['./analytics.component.css'],
    standalone: true,
    imports: [
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        NgbNavContent,
        FormsModule,
        WjInputModule,
        WjGridModule,
        WjGridFilterModule,
        AutoSizeDirective,
        WjChartModule,
        ActionToolbarCount,
        ActionToolbarSearchComponent,
        GridSelectionCalculationComponent,
        EstimateVsActualComponent,
        MarginEstVsActualComponent,
        adjGrossMarginPctComponent,
        NgbNavOutlet,
    ],
})
export class AnalyticsComponent extends BaseComponent {
    @ViewChild('amountChart') amountChart!: FlexChart
    @ViewChild('ratioChart') ratioChart!: FlexChart
    faSearch = faSearch
    active = 1
    analyticGrid: Grid<any>
    competitionGrid: Grid<any>
    timeSeriesGrid: Grid<any>
    compTimeSeriesGrid: Grid<any>
    seasonalGrid: Grid<any>
    whatIfGrid: Grid<any>
    whatIfGridDetails: Grid<any>
    wonByDeptGrid: Grid<any>
    wonByDeptDepartments: { code: string; name: string }[] = []
    wonByDeptTimeunit = 6
    wonByDeptEndDate = new Date()
    wonByDeptStartDate = new Date(this.wonByDeptEndDate.getFullYear() - 5, 0, 1)
    winPercentData: any[] = []
    timeunits: ListItem<number>[] = []
    selectedTimeunit = 6
    maxBidRange = 0
    maxAmountRange = 0
    maxRationRange = 0
    seasonsMap = [new ListItem(0, 'Q1'), new ListItem(1, 'Q2'), new ListItem(2, 'Q3'), new ListItem(3, 'Q4')]
    selectedSeasons: any
    projectTypesMap: ListItem<string>[] = []
    selectedProjectTypes: any
    projectTypesMapWhatIf: ListItem<string>[] = []
    selectedProjectTypesWhatIf: any
    compCountMap: ListItem<number>[] = []
    selecteCompCounts: any
    competitorMap: ListItem<number>[] = []
    selectedCompetitor = -1
    loadingComplete = false
    isSaving = false
    whatIf =
        '1.01, 1.02, 1.03, 1.04, 1.05, 1.06, 1.07, 1.08, 1.09, 1.1, 1.11, 1.12, 1.13, 1.14, 1.15, 1.16, 1.17, 1.18, 1.19, 1.2, .99, .98, .97, .96, .95, .94, .93, .92, .91, .90'

    endDate = new Date()
    startDate = new Date(this.endDate.getFullYear() - 5, 0, 1)

    get vanConHeader(): string {
        return this.selectedCompetitor === -1
            ? 'VanCon Bidding'
            : 'VanCon Bidding When ' + this.competitorMap[this.selectedCompetitor].display + ' Also Bids'
    }
    get competitorHeader(): string {
        return this.selectedCompetitor === -1
            ? 'Select a Competitor Above'
            : this.competitorMap[this.selectedCompetitor].display + ' Bidding'
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.BidAnalyticsEdit])
    }

    constructor() {
        super()
        this.analyticGrid = new Grid<any>(this.services).wrapColumnHeaders().filterBy(item => [item.bidType])

        this.competitionGrid = new Grid<any>(this.services).wrapColumnHeaders().filterBy(item => [item.name])

        this.timeSeriesGrid = new Grid<any>(this.services).wrapColumnHeaders().filterBy(item => [item.name])

        this.compTimeSeriesGrid = new Grid<any>(this.services).wrapColumnHeaders().filterBy(item => [item.name])

        this.seasonalGrid = new Grid<any>(this.services).wrapColumnHeaders()

        this.whatIfGrid = new Grid<any>(this.services).wrapColumnHeaders()
        this.whatIfGrid.selectionChanged.on(this.updateWhatIfDetails.bind(this))

        this.whatIfGridDetails = new Grid<any>(this.services).wrapColumnHeaders()

        this.wonByDeptGrid = new Grid<any>(this.services).wrapColumnHeaders()

        this.timeunits.push(new ListItem(6, 'Quarterly'))
        this.timeunits.push(new ListItem(5, 'Monthly'))
        this.timeunits.push(new ListItem(7, 'Yearly'))

        for (let i = 0; i < 10; i++) {
            this.compCountMap.push(new ListItem(i + 1, i === 9 ? '>9' : (i + 1).toString()))
        }
        this.loadData()
    }
    loadData() {
        const params = new HttpParams().set('filterOnBids', true)
        this.cancelableRequest(
            'data',
            true,
            this.services.data.load(true, true, this.services.data.get('/api/VpOwner/GetCompetitors', params)).subscribe({
                next: result => {
                    this.competitorMap = result[0].map(x => new ListItem(x.id, x.name))
                    setTimeout(() => {
                        this.selectedCompetitor = -1
                        setTimeout(() => {
                            this.loadingComplete = true
                            this.refresh()
                        }, 10)
                    }, 10)
                },
            }),
        )
    }
    refresh() {
        if (!this.loadingComplete) return
        const compId = this.selectedCompetitor > -1 ? this.competitorMap[this.selectedCompetitor].value : -2
        const params = new HttpParams()
            .set('timeUnit', this.selectedTimeunit)
            .set('compId', compId)
            .set('startDate', this.startDate.toLocaleDateString())
            .set('endDate', this.endDate.toLocaleDateString())
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.get('/api/Analytics/GetAnalytics', params),
                    this.services.data.get('/api/VpData/QueryProjectTypes'),
                    this.services.data.jobTypes.queryAll(),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        this.analyticGrid.items = result[0].analytics
                        this.winPercentData = result[0].winPercents
                        this.competitionGrid.items = result[0].competitors
                        this.timeSeriesGrid.items = result[0].timeSeries
                        this.compTimeSeriesGrid.items = result[0].timeSeries

                        this.maxBidRange = 0
                        this.timeSeriesGrid.items.forEach(x => {
                            if (x.totalBidCount > this.maxBidRange) this.maxBidRange = x.totalBidCount
                        })
                        this.maxBidRange *= 1.25

                        this.maxAmountRange = 0
                        this.timeSeriesGrid.items.forEach(x => {
                            if (x.totalBidAmount > this.maxAmountRange) this.maxAmountRange = x.totalBidAmount
                        })
                        this.maxAmountRange *= 1.25

                        this.maxRationRange = 0
                        this.timeSeriesGrid.items.forEach(x => {
                            if (x.totalWinAmountRatio > this.maxRationRange) this.maxRationRange = x.totalWinAmountRatio
                        })
                        this.maxRationRange *= 1.25

                        this.amountChart.dataLabel.content = this.amountLabel
                        this.ratioChart.dataLabel.content = this.ratiotLabel

                        const projectTypes = result[1]
                        this.projectTypesMap = projectTypes.map(x => new ListItem(x.databaseValue, x.databaseValue))
                        this.projectTypesMapWhatIf = projectTypes.map(x => new ListItem(x.databaseValue, x.databaseValue))
                    },
                }),
        )
    }
    tabChanged() {
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'))
        }, 50)
    }
    amountLabel = ht => {
        return (Math.round(ht.value / 100000) / 10).toFixed(1) + 'M'
    }
    ratiotLabel = ht => {
        return (ht.value * 100).toFixed(1) + '%'
    }
    amountChartItemAxisFormatter = (engine: IRenderEngine, label: any) => {
        label.text = '$' + (label.val / 1000000).toFixed(0) + 'M'
        return label
    }
    getSeasonalBidReport() {
        this.seasonalGrid.items = []

        const seasons = this.selectedSeasons.map(x => x.value)
        const projectTypes = this.selectedProjectTypes.map(x => x.value)
        const compCounts = this.selecteCompCounts.map(x => x.value)
        const dto = {
            seasons: seasons,
            projectTypes: projectTypes,
            compCounts: compCounts,
        }

        this.cancelableRequest(
            'seasonalData',
            true,
            this.services.data.load(true, true, this.services.data.post('/api/Analytics/QuerySeasonalAnalytics', dto)).subscribe({
                next: result => {
                    this.seasonalGrid.items = result[0]
                },
            }),
        )
    }
    calculateWhatIf() {
        if (!this.selectedProjectTypesWhatIf) {
            this.services.notification.warning('Please select one or more bid types')
            return
        }
        this.isSaving = true
        const queryDto = {
            percentsString: this.whatIf,
            projectTypes: this.selectedProjectTypesWhatIf.map(x => x.value),
        }
        this.cancelableRequest(
            '',
            true,
            this.services.data.load(true, true, this.services.data.post('/api/Analytics/GetBidWhatIf', queryDto)).subscribe({
                next: result => {
                    this.whatIfGrid.items = result[0]
                },
                complete: () => (this.isSaving = false),
            }),
        )
    }
    whatIfItemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const col = panel.grid.columns[c]
            const row = panel.grid.rows[r]
            const dataItem = row.dataItem

            if (col.binding === 'delta') {
                cell.innerHTML = this.formatter.format(dataItem.winRevenue - this.whatIfGrid.items[0].winRevenue)
            }

            if (col.binding === 'deltaMarkUp') {
                cell.innerHTML = this.formatter.format(dataItem.markUp - this.whatIfGrid.items[0].markUp)
            }
        }
    }
    whatIfGridDetailsItemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const col = panel.grid.columns[c]
            const row = panel.grid.rows[r]
            const dataItem = row.dataItem
            const baseItem = this.whatIfGrid.items[0].bids.find(x => x.id == dataItem.id)

            cell.classList.remove('green-cell')
            cell.classList.remove('red-cell')
            cell.classList.remove('yellow-cell')
            if (baseItem.vanCon <= baseItem.low && dataItem.vanCon > baseItem.secondPlaceBid) {
                cell.classList.add('red-cell')
            }
            if (baseItem.vanCon > baseItem.low && dataItem.vanCon < baseItem.low) {
                cell.classList.add('green-cell')
            } else if (dataItem.vanCon <= dataItem.low) {
                cell.classList.add('yellow-cell')
            }

            if (col.binding === 'oldVanCon') {
                cell.innerHTML = this.formatter.format(baseItem.vanCon)
            }
            if (col.binding === 'oldLow') {
                cell.innerHTML = this.formatter.format(baseItem.low)
            }
            if (col.binding === 'oldMarkUp') {
                cell.innerHTML = this.formatter.format(baseItem.markUp)
            }
        }
    }
    updateWhatIfDetails() {
        if (!this.whatIfGrid.selectedItem) {
            this.whatIfGridDetails.items = []
            return
        }
        const whatIf = this.whatIfGrid.selectedItem
        this.whatIfGridDetails.items = whatIf.bids
    }

    refreshWonByDept() {
        const params = new HttpParams()
            .set('timeUnit', this.wonByDeptTimeunit)
            .set('startDate', this.wonByDeptStartDate.toLocaleDateString())
            .set('endDate', this.wonByDeptEndDate.toLocaleDateString())
        this.cancelableRequest(
            'wonByDept',
            true,
            this.services.data.load(true, true, this.services.data.get('/api/Analytics/GetWonByDepartment', params)).subscribe({
                next: result => {
                    const data = result[0] as { rows: any[]; departments: { code: string; name: string }[] }
                    this.wonByDeptDepartments = data.departments

                    const flatRows = data.rows.map(row => {
                        const flat: any = {
                            date: row.date,
                            wonAmount: row.wonAmount,
                            margin: row.margin,
                            netCost: row.netCost,
                            hcssDirectTotal: row.hcssDirectTotal ?? 0,
                            materialsCost: row.materialsCost ?? 0,
                            subcontractCost: row.subcontractCost ?? 0,
                            bondTotal: row.bondTotal ?? 0,
                            addonCost: row.addonCost ?? 0,
                        }
                        for (const dept of data.departments) {
                            flat['dept_' + dept.code] = row.departmentAllocations?.[dept.code] ?? 0
                        }
                        return flat
                    })

                    this.buildWonByDeptColumns(data.departments)
                    this.wonByDeptGrid.items = flatRows
                },
            }),
        )
    }

    private buildWonByDeptColumns(departments: { code: string; name: string }[]) {
        const grid = this.wonByDeptGrid.flexGrid
        if (!grid) return

        grid.autoGenerateColumns = false
        grid.columns.clear()
        grid.columns.push(new Column({ header: 'Date', binding: 'date', width: 100 }))
        grid.columns.push(new Column({ header: 'Won Amount', binding: 'wonAmount', width: 130, format: 'C0' }))
        grid.columns.push(new Column({ header: 'Margin', binding: 'margin', width: 120, format: 'C0' }))
        grid.columns.push(new Column({ header: 'Net Cost', binding: 'netCost', width: 120, format: 'C0' }))
        grid.columns.push(new Column({ header: 'HCSS Direct Total', binding: 'hcssDirectTotal', width: 150, format: 'C0' }))
        grid.columns.push(new Column({ header: 'Materials (Perm + Const)', binding: 'materialsCost', width: 170, format: 'C0' }))
        grid.columns.push(new Column({ header: 'Subcontract', binding: 'subcontractCost', width: 130, format: 'C0' }))
        grid.columns.push(new Column({ header: 'Bond Total', binding: 'bondTotal', width: 130, format: 'C0' }))
        grid.columns.push(new Column({ header: 'Addon Cost', binding: 'addonCost', width: 120, format: 'C0' }))
        for (const dept of departments) {
            grid.columns.push(new Column({ header: dept.name, binding: 'dept_' + dept.code, width: 130, format: 'C0' }))
        }
    }

    formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',

        // These options are needed to round to whole numbers if that's what you want.
        minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
        maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
    })
}
